import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  useTheme,
  LinearProgress,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  HourglassBottom as HourglassIcon,
  Help as HelpIcon,
} from "@mui/icons-material";
import { NoticeboardButton } from "../shared";
import VideoPlayerModal from "../VideoPlayerModal";
import { useNavigate } from "react-router-dom";
import { lightTheme } from "../../styles/theme";

interface ActionCard {
  title: string;
  description: string;
  buttonLabel: string;
  onClick: () => void;
  image?: string;
}

interface PendingApprovalProps {
  onViewProfile: () => void;
}

const PendingApproval: React.FC<PendingApprovalProps> = ({ onViewProfile }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedVideo, setSelectedVideo] = useState<{
    videoPath: string;
    title: string;
  } | null>(null);

  const steps = [
    { label: "Account Created", done: true },
    { label: "Details Submitted", done: true },
    { label: "Verification in Progress", done: false },
  ];

  const actionCards: ActionCard[] = [
    {
      title: "Complete Your Profile",
      description:
        "Add your logo and circle details so your notices feel official from day one.",
      buttonLabel: "Finish Profile",
      onClick: onViewProfile,
      image: "/home/completeprofile.webp",
    },
    {
      title: "How to Create a Notice",
      description:
        "Learn how to create and send your first notice to keep everyone informed.",
      buttonLabel: "Watch Video",
      onClick: () =>
        setSelectedVideo({
          videoPath: "/videos/createnotice.webm",
          title: "How to Create a Notice",
        }),
      image: "/home/nonotices.webp",
    },
    {
      title: "Learn How It Works",
      description:
        "Discover how circles use Noticeboard to keep users informed and engaged.",
      buttonLabel: "Visit Help Center",
      onClick: () => navigate("/help/videos"),
      image: "/home/howitworks.webp",
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Hero Banner */}
      <Paper
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.hero.gradientStart} 0%, ${theme.palette.hero.gradientMid} 50%, ${theme.palette.hero.gradientEnd} 100%)`,
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: { xs: 3, md: 0 },
          }}
        >
          {/* Left content */}
          <Box
            sx={{
              flex: 1,
              p: { xs: 3, md: 4 },
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  bgcolor: lightTheme.colors.whiteOverlay25,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircleIcon sx={{ color: lightTheme.colors.white, fontSize: 22 }} />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: lightTheme.colors.white,
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                }}
              >
                Your Account Is Being Verified
              </Typography>
            </Box>

            {/* Info card */}
            <Paper
              elevation={0}
              sx={{
                bgcolor: lightTheme.colors.whiteOverlay45,
                backdropFilter: "blur(12px)",
                borderRadius: 2.5,
                p: { xs: 2, md: 2.5 },
                border: `1px solid ${lightTheme.colors.whiteOverlay50}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: lightTheme.colors.warningAmberTint20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircleIcon
                    sx={{ color: lightTheme.colors.warningLight, fontSize: 20 }}
                  />
                </Box>
                <Typography sx={{ fontWeight: 700, color: "text.primary", fontSize: "1rem" }}>
                  Account Under Review
                </Typography>
              </Box>

              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "0.9rem",
                  lineHeight: 1.6,
                  mb: 1,
                }}
              >
                We're reviewing your organization to ensure Noticeboard remains a safe
                and trusted space for families.
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "0.9rem",
                  lineHeight: 1.6,
                  mb: 2,
                }}
              >
                This usually takes less than 24 hours.
              </Typography>

              {/* Status steps */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {steps.map((step) => (
                  <Box
                    key={step.label}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                  >
                    {step.done ? (
                      <CheckCircleIcon
                        sx={{ color: theme.palette.icons.green, fontSize: 22 }}
                      />
                    ) : (
                      <HourglassIcon
                        sx={{ color: lightTheme.colors.warningLight, fontSize: 22 }}
                      />
                    )}
                    <Typography
                      sx={{
                        color: "text.primary",
                        fontSize: "0.9rem",
                        fontWeight: step.done ? 500 : 700,
                      }}
                    >
                      {step.label}
                    </Typography>
                    {!step.done && (
                      <LinearProgress
                        sx={{
                          flex: 1,
                          maxWidth: 100,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: lightTheme.colors.shadowMedium,
                          "& .MuiLinearProgress-bar": {
                            bgcolor: "primary.main",
                            borderRadius: 3,
                          },
                        }}
                        variant="indeterminate"
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>

          {/* Right image */}
          <Box
            sx={{
              flex: { xs: "none", md: 1 },
              display: { xs: "none", md: "flex" },
              justifyContent: "center",
              alignItems: "flex-end",
              pr: { md: 2 },
            }}
          >
            <Box
              component="img"
              src="/home/pending.webp"
              alt="Account verification"
              sx={{
                maxWidth: { sm: 360, md: 420 },
                width: "100%",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Info bar */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          bgcolor: theme.palette.cards?.orangeBg || lightTheme.colors.cardOrangeHover,
          border: "1px solid",
          borderColor: theme.palette.warning.light,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            Most accounts are approved within 24 hours.
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            We'll notify you the moment you're ready to go.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
          <NoticeboardButton
            variant="primary"
            onClick={onViewProfile}
            sx={{
              bgcolor: theme.palette.warning.main,
              color: lightTheme.colors.white,
              "&:hover": { bgcolor: theme.palette.warning.dark },
            }}
          >
            Continue Setup
          </NoticeboardButton>
          <NoticeboardButton
            variant="secondary"
            onClick={() => navigate("/help/videos")}
            startIcon={<HelpIcon />}
            sx={{
              borderColor: theme.palette.warning.main,
              color: theme.palette.warning.dark,
              "&:hover": {
                bgcolor: `${theme.palette.warning.main}14`,
                borderColor: theme.palette.warning.dark,
              },
            }}
          >
            Help Center
          </NoticeboardButton>
        </Box>
      </Paper>

      {/* Get Ready While You Wait */}
      <Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}
        >
          Get Ready While You Wait
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", mb: 3 }}
        >
          While we review your account, you can start setting things up.
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
            gap: 2.5,
          }}
        >
          {actionCards.map((card) => (
            <Paper
              key={card.title}
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                transition: "box-shadow 0.2s, border-color 0.2s",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 4px 20px ${lightTheme.colors.primaryTint10}`,
                },
              }}
            >
              {card.image && (
                <Box
                  component="img"
                  src={card.image}
                  alt={card.title}
                  loading="lazy"
                  decoding="async"
                  sx={{
                    width: "100%",
                    height: 140,
                    objectFit: "contain",
                    borderRadius: 2,
                    mb: 0.5,
                  }}
                />
              )}
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "text.primary" }}
              >
                {card.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  lineHeight: 1.6,
                  flex: 1,
                }}
              >
                {card.description}
              </Typography>
            <NoticeboardButton
              variant="primary"
              onClick={card.onClick}
              sx={{ alignSelf: "flex-end", mt: 1 }}
            >
                {card.buttonLabel}
              </NoticeboardButton>
            </Paper>
          ))}
        </Box>
      </Box>

      <VideoPlayerModal
        open={selectedVideo !== null}
        onClose={() => setSelectedVideo(null)}
        videoPath={selectedVideo?.videoPath || ""}
        title={selectedVideo?.title || ""}
      />
    </Box>
  );
};

export default PendingApproval;
